import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users, Target, DollarSign, CheckCircle, Calendar, UserCheck,
  ChevronDown, ChevronRight, ArrowUpDown, TrendingUp,
  Eye, MousePointerClick, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * MA-CAMPAIGN-SURFACE-ALIGNMENT-028: Canonical Campaign Analytics
 *
 * Powered exclusively by /api/profit-path-analytics/campaigns (canonical campaign_id).
 * No raw-string grouping. No legacy endpoints.
 */

const KPI = ({ icon: Icon, label, value, color = "text-slate-100" }) => (
  <div className="flex flex-col gap-1 rounded-lg bg-white/5 border border-white/10 px-4 py-3 min-w-[130px]" data-testid={`campaign-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-1.5 text-xs text-slate-400">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <div className={`text-lg font-semibold ${color}`}>{value}</div>
  </div>
);

const OUTCOME_COLORS = {
  qualified: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  not_qualified: "bg-red-500/20 text-red-300 border-red-500/30",
  abandoned: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  no_consent: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  error: "bg-rose-500/20 text-rose-300 border-rose-500/30",
};

const OutcomePills = ({ outcomes }) => {
  if (!outcomes) return null;
  const items = [
    { key: "qualified", label: "Qual", count: outcomes.qualified_count },
    { key: "not_qualified", label: "NQ", count: outcomes.not_qualified_count },
    { key: "abandoned", label: "Drop", count: outcomes.abandoned_count },
    { key: "no_consent", label: "NC", count: outcomes.no_consent_count },
    { key: "error", label: "Err", count: outcomes.error_count },
  ].filter((i) => i.count > 0);

  if (items.length === 0) return <span className="text-xs text-slate-500">-</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {items.map((i) => (
        <Badge key={i.key} variant="outline" className={`text-[10px] px-1.5 py-0 ${OUTCOME_COLORS[i.key]}`}>
          {i.label} {i.count}
        </Badge>
      ))}
    </div>
  );
};

const SortHeader = ({ label, field, sortField, sortDir, onSort }) => (
  <th
    className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 cursor-pointer hover:text-slate-200 whitespace-nowrap select-none"
    onClick={() => onSort(field)}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {sortField === field && (
        <ArrowUpDown className={`h-3 w-3 ${sortDir === "asc" ? "rotate-180" : ""}`} />
      )}
    </span>
  </th>
);

export default function CanonicalCampaignAnalytics({ period = "30d", campaignId, tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("total_revenue");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const tenantParam = tenantId ? `&tenant_id=${tenantId}` : "";
        const res = await api.get(
          `/profit-path-analytics/campaigns?period=${period}${tenantParam}`
        );
        setData(res.data);
      } catch (e) {
        console.error("Failed to fetch canonical campaign analytics:", e);
        setError(e.response?.data?.detail || "Failed to load campaign analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period, tenantId]);

  // Fetch funnel breakdown when a campaign is expanded
  useEffect(() => {
    if (!expandedCampaign) {
      setFunnelData(null);
      return;
    }
    const fetchFunnels = async () => {
      setFunnelLoading(true);
      try {
        const tenantParam = tenantId ? `&tenant_id=${tenantId}` : "";
        const res = await api.get(
          `/profit-path-analytics/funnels?period=${period}${tenantParam}`
        );
        setFunnelData(res.data?.funnels || []);
      } catch {
        setFunnelData([]);
      } finally {
        setFunnelLoading(false);
      }
    };
    fetchFunnels();
  }, [expandedCampaign, period, tenantId]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const campaigns = useMemo(() => {
    if (!data?.campaigns) return [];
    let list = [...data.campaigns];

    // Filter by selected campaign if specified
    if (campaignId && campaignId !== "all") {
      list = list.filter((c) => c.campaign_id === campaignId);
    }

    list.sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [data, sortField, sortDir, campaignId]);

  // Split measured vs unmapped
  const measuredCampaigns = useMemo(() => campaigns.filter(c => c.measurement_status !== "no_profit_paths"), [campaigns]);
  const unmappedCampaigns = useMemo(() => campaigns.filter(c => c.measurement_status === "no_profit_paths"), [campaigns]);

  // Aggregated KPIs (only from measured campaigns)
  const kpis = useMemo(() => {
    const list = measuredCampaigns;
    const totalImpressions = list.reduce((s, c) => s + (c.source_metrics?.impressions || 0), 0);
    const totalClicks = list.reduce((s, c) => s + (c.source_metrics?.clicks || 0), 0);
    const totalSpend = list.reduce((s, c) => s + (c.source_metrics?.spend || 0), 0);
    return {
      total: list.length,
      unmapped: unmappedCampaigns.length,
      leads: list.reduce((s, c) => s + c.lead_count, 0),
      qualified: list.reduce((s, c) => s + c.qualified_count, 0),
      appointments: list.reduce((s, c) => s + c.appointment_count, 0),
      clients: list.reduce((s, c) => s + c.client_count, 0),
      revenue: list.reduce((s, c) => s + c.total_revenue, 0),
      qualRate:
        list.reduce((s, c) => s + c.lead_count, 0) > 0
          ? ((list.reduce((s, c) => s + c.qualified_count, 0) / list.reduce((s, c) => s + c.lead_count, 0)) * 100).toFixed(1)
          : "0.0",
      rpl:
        list.reduce((s, c) => s + c.lead_count, 0) > 0
          ? (list.reduce((s, c) => s + c.total_revenue, 0) / list.reduce((s, c) => s + c.lead_count, 0)).toFixed(2)
          : "0.00",
      impressions: totalImpressions,
      clicks: totalClicks,
      spend: totalSpend,
    };
  }, [measuredCampaigns, unmappedCampaigns]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400" data-testid="campaign-analytics-loading">
        <div className="animate-pulse text-sm">Loading canonical campaign analytics...</div>
      </div>
    );
  }

  if (error) {
    // MA-PHASE11A-WARROOM-FINAL-VALIDATION-021: Neutral error state
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="py-12 text-center" data-testid="campaign-analytics-error">
          <Target className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">Campaign Analytics Unavailable</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Unable to load campaign performance data at this time. This may be due to missing campaign sync or insufficient data for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!campaigns.length) {
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="py-12 text-center" data-testid="campaign-analytics-empty">
          <Target className="h-10 w-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No campaigns included in measurement yet</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Sync campaigns from Meta and choose which ones to track in MissionAssist.
            Go to Settings &rarr; Meta Sync to manage campaign inclusion.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="canonical-campaign-analytics">
      {/* KPI Row */}
      <div className="flex flex-wrap gap-3" data-testid="campaign-kpi-row">
        <KPI icon={Target} label="Measured" value={kpis.total} />
        {kpis.unmapped > 0 && (
          <KPI icon={Target} label="Unmapped" value={kpis.unmapped} color="text-amber-300" />
        )}
        <KPI icon={Users} label="Total Leads" value={kpis.leads.toLocaleString()} />
        <KPI icon={CheckCircle} label="Qualified" value={kpis.qualified.toLocaleString()} color="text-emerald-300" />
        <KPI icon={Calendar} label="Appointments" value={kpis.appointments.toLocaleString()} />
        <KPI icon={UserCheck} label="Clients" value={kpis.clients.toLocaleString()} />
        <KPI icon={DollarSign} label="Revenue" value={`$${kpis.revenue.toLocaleString()}`} color="text-emerald-300" />
        <KPI icon={TrendingUp} label="Qual Rate" value={`${kpis.qualRate}%`} />
        <KPI icon={DollarSign} label="Rev / Lead" value={`$${kpis.rpl}`} />
        {(kpis.impressions > 0 || kpis.spend > 0) && (
          <>
            <KPI icon={Eye} label="Impressions" value={kpis.impressions.toLocaleString()} color="text-sky-300" />
            <KPI icon={MousePointerClick} label="Clicks" value={kpis.clicks.toLocaleString()} color="text-sky-300" />
            <KPI icon={Wallet} label="Spend" value={`$${kpis.spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color="text-amber-300" />
          </>
        )}
      </div>

      {/* Campaign Performance Table */}
      <Card className="bg-white/5 border-white/10 overflow-hidden">
        <CardHeader className="py-3 px-4 border-b border-white/5">
          <CardTitle className="text-sm font-medium text-slate-200 flex items-center gap-2">
            <Target className="h-4 w-4 text-indigo-400" />
            Campaign Performance
            <Badge variant="outline" className="ml-auto text-[10px] text-slate-400 border-slate-600">
              Canonical
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="campaign-performance-table">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 w-8"></th>
                  <SortHeader label="Campaign" field="campaign_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400">Platform</th>
                  <SortHeader label="Leads" field="lead_count" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Qual" field="qualified_count" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Qual %" field="qualification_rate" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Appts" field="appointment_count" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Clients" field="client_count" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Revenue" field="total_revenue" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Rev/Lead" field="revenue_per_lead" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Rev/Appt" field="revenue_per_appointment" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400">Outcomes</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-sky-400/70 border-l border-white/5">Impr</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-sky-400/70">Clicks</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-amber-400/70">Spend</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-sky-400/70">CTR</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-amber-400/70">CPC</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-amber-400/70">CPM</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-violet-400/70 border-l border-white/5">ROAS</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-violet-400/70">CAC</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-violet-400/70">CPL</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-violet-400/70">CPQL</th>
                </tr>
              </thead>
              <tbody>
                {measuredCampaigns.map((c) => (
                  <React.Fragment key={c.campaign_id}>
                    <tr
                      className="border-b border-white/5 hover:bg-white/[0.03] transition-colors cursor-pointer"
                      onClick={() => setExpandedCampaign(expandedCampaign === c.campaign_id ? null : c.campaign_id)}
                      data-testid={`campaign-row-${c.campaign_id}`}
                    >
                      <td className="px-3 py-2.5 text-slate-500">
                        {expandedCampaign === c.campaign_id ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-200 font-medium max-w-[200px] truncate" title={c.campaign_name}>
                        <div className="flex items-center gap-2">
                          {c.campaign_name}
                          {c.profit_path_count > 0 && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 text-slate-500 border-slate-700">
                              {c.profit_path_count} PP{c.profit_path_count !== 1 ? "s" : ""}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        {c.source_platform && (
                          <Badge variant="outline" className="text-[10px] text-slate-300 border-slate-600 capitalize">
                            {c.source_platform}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-300">{c.lead_count}</td>
                      <td className="px-3 py-2.5 text-emerald-300">{c.qualified_count}</td>
                      <td className="px-3 py-2.5 text-slate-300">{c.qualification_rate}%</td>
                      <td className="px-3 py-2.5 text-slate-300">{c.appointment_count}</td>
                      <td className="px-3 py-2.5 text-slate-300">{c.client_count}</td>
                      <td className="px-3 py-2.5 text-emerald-300 font-medium">${c.total_revenue.toLocaleString()}</td>
                      <td className="px-3 py-2.5 text-slate-300">${c.revenue_per_lead}</td>
                      <td className="px-3 py-2.5 text-slate-300">${c.revenue_per_appointment}</td>
                      <td className="px-3 py-2.5">
                        <OutcomePills outcomes={c.outcomes} />
                      </td>
                      <td className="px-3 py-2.5 text-sky-300/80 border-l border-white/5 tabular-nums">
                        {c.source_metrics ? c.source_metrics.impressions.toLocaleString() : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-3 py-2.5 text-sky-300/80 tabular-nums">
                        {c.source_metrics ? c.source_metrics.clicks.toLocaleString() : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-3 py-2.5 text-amber-300/80 tabular-nums">
                        {c.source_metrics ? `$${c.source_metrics.spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-3 py-2.5 text-sky-300/80 tabular-nums">
                        {c.source_metrics?.ctr != null ? `${(c.source_metrics.ctr * 100).toFixed(2)}%` : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-3 py-2.5 text-amber-300/80 tabular-nums">
                        {c.source_metrics?.cpc != null ? `$${c.source_metrics.cpc.toFixed(2)}` : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-3 py-2.5 text-amber-300/80 tabular-nums">
                        {c.source_metrics?.cpm != null ? `$${c.source_metrics.cpm.toFixed(2)}` : <span className="text-slate-600">-</span>}
                      </td>
                      <td className={`px-3 py-2.5 tabular-nums border-l border-white/5 ${c.roas != null ? (c.roas >= 3 ? 'text-emerald-300' : c.roas >= 1 ? 'text-slate-300' : 'text-red-300') : ''}`}>
                        {c.roas != null ? `${c.roas.toFixed(2)}x` : <span className="text-slate-600">-</span>}
                      </td>
                      <td className={`px-3 py-2.5 tabular-nums ${c.cac != null ? 'text-violet-300/80' : ''}`}>
                        {c.cac != null ? `$${c.cac.toFixed(2)}` : <span className="text-slate-600">-</span>}
                      </td>
                      <td className={`px-3 py-2.5 tabular-nums ${c.cpl != null ? 'text-violet-300/80' : ''}`}>
                        {c.cpl != null ? `$${c.cpl.toFixed(2)}` : <span className="text-slate-600">-</span>}
                      </td>
                      <td className={`px-3 py-2.5 tabular-nums ${c.cpql != null ? 'text-violet-300/80' : ''}`}>
                        {c.cpql != null ? `$${c.cpql.toFixed(2)}` : <span className="text-slate-600">-</span>}
                      </td>
                    </tr>

                    {/* Funnel Drill-In */}
                    {expandedCampaign === c.campaign_id && (
                      <tr data-testid={`campaign-funnel-drilldown-${c.campaign_id}`}>
                        <td colSpan={22} className="bg-white/[0.02] px-6 py-3">
                          {funnelLoading ? (
                            <div className="text-xs text-slate-500 animate-pulse py-2">Loading funnels...</div>
                          ) : funnelData && funnelData.length > 0 ? (
                            <div>
                              <p className="text-xs text-slate-400 mb-2 font-medium">Funnels feeding this campaign</p>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-500">
                                    <th className="text-left py-1 px-2">Funnel</th>
                                    <th className="text-left py-1 px-2">Leads</th>
                                    <th className="text-left py-1 px-2">Qual</th>
                                    <th className="text-left py-1 px-2">Appts</th>
                                    <th className="text-left py-1 px-2">Revenue</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {funnelData.map((f) => (
                                    <tr key={f.funnel_id} className="border-t border-white/5 text-slate-300">
                                      <td className="py-1.5 px-2">{f.funnel_name}</td>
                                      <td className="py-1.5 px-2">{f.lead_count}</td>
                                      <td className="py-1.5 px-2 text-emerald-300">{f.qualified_count}</td>
                                      <td className="py-1.5 px-2">{f.appointment_count}</td>
                                      <td className="py-1.5 px-2 text-emerald-300">${f.total_revenue.toLocaleString()}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-xs text-slate-500 py-1">No funnel data available for this period</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}

                {/* Unmapped campaigns — clear no-measurement state */}
                {unmappedCampaigns.length > 0 && (
                  <>
                    <tr className="border-b border-white/5">
                      <td colSpan={22} className="px-3 py-2 bg-amber-500/[0.03]">
                        <span className="text-xs font-medium text-amber-400/80">
                          No Profit Paths mapped ({unmappedCampaigns.length} campaign{unmappedCampaigns.length !== 1 ? "s" : ""})
                        </span>
                      </td>
                    </tr>
                    {unmappedCampaigns.map((c) => (
                      <tr key={c.campaign_id} className="border-b border-white/5 opacity-50" data-testid={`campaign-unmapped-${c.campaign_id}`}>
                        <td className="px-3 py-2.5"></td>
                        <td className="px-3 py-2.5 text-slate-400 max-w-[200px] truncate" title={c.campaign_name}>
                          {c.campaign_name}
                        </td>
                        <td className="px-3 py-2.5">
                          {c.source_platform && (
                            <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-700 capitalize">
                              {c.source_platform}
                            </Badge>
                          )}
                        </td>
                        <td colSpan={19} className="px-3 py-2.5 text-xs text-amber-400/60">
                          No ads mapped to Profit Paths yet
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
